// LiveSpreadsPanel · OFinancial real-time spread feed.
// Top-level category tabs (Forex / Metals / Indices / Crypto / Energies / Shares)
// reveal a SECOND row of subcategory chips (Majors / Minors / Crosses / Exotics
// for FX · Major / Minor for Crypto · USA / UK / Germany / France / Lisbon / UAE / HK / etc for Shares).
// Mobile collapses the table to 3 columns (Symbol · Bid · Change) so prices never overlap.
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, TrendingUp, TrendingDown, Wifi, WifiOff, ChevronLeft, ChevronRight,
  Coins, Gem, BarChart3, Bitcoin, Flame, Building2, CircleDollarSign,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import useLivePrices from "@/hooks/useLivePrices";
import api from "@/lib/api";
import SymbolIcon from "@/components/SymbolIcon";

const CATEGORIES = ["All", "Forex", "Metals", "Indices", "Crypto", "Energies", "Shares"];
const CATEGORY_I18N_KEY = {
  All: "all", Forex: "forex", Metals: "metals", Indices: "indices",
  Crypto: "crypto", Energies: "energies", Shares: "shares",
};

const CAT_ICON = {
  Forex:       Coins,
  Metals:      Gem,
  Indices:     BarChart3,
  Crypto:      Bitcoin,
  Energies:    Flame,
  Commodities: Flame,
  Shares:      Building2,
  Bonds:       BarChart3,
};
const SYMBOL_ICON = {
  BTCUSD: Bitcoin, ETHUSD: Bitcoin, XAUUSD: Gem, XAGUSD: Gem,
  XPTUSD: Gem, XPDUSD: Gem, USOIL: Flame, UKOIL: Flame, NGAS: Flame,
};

// Per-symbol leverage tier · matches the broker's published terms.
// FX majors/minors get the highest dynamic ratio (up to 1:1000), shares
// get the conservative fixed 1:10, indices/energies sit at 1:100, crypto
// sits at 1:20 and metals at 1:500.
const INDEX_LEV_PATTERN = /^(WS30|SP500|NAS100|GER40|DAX40|UK100|JPN225|HSI50|HK50|FRA40|CAC40|AUS200|AEX25|SWI20|SMI20|ESP35|IBEX35|ITA40|NETH25|EU50|ESX50|SA40|RUS2K|CHINA50|IND50|SPX500)/;
function leverageFor(sym, category) {
  const cat = (category || "").toLowerCase();
  if (cat === "shares" || /\.(US|UK|EU|AU|HK|JP|DE|FR|IT|ES|CH|SA)$/.test(sym)) return { ratio: "1:10", upTo: false };
  if (cat === "crypto" || sym === "BTCUSD" || sym === "ETHUSD") return { ratio: "1:20", upTo: false };
  if (cat === "indices" || INDEX_LEV_PATTERN.test(sym)) return { ratio: "1:100", upTo: false };
  if (cat === "energies" || sym === "USOIL" || sym === "UKOIL" || sym === "NGAS") return { ratio: "1:100", upTo: false };
  if (cat === "metals" || /^XAU|^XAG|^XPT|^XPD/.test(sym)) return { ratio: "1:500", upTo: true };
  // Forex (default)
  return { ratio: "1:1000", upTo: true };
}

// Subcategory chip i18n keys · slug used to resolve homepage.spreads.subLabels.<slug>
// Display falls back to the slug name itself if the key is missing.
const SUB_KEYS = [
  "majors", "minors", "crosses", "exotics", "weekend", "liquidity",
  "major", "minor", "conversions", "usdt",
  "usa", "uk", "germany", "france", "switzerland", "netherlands",
  "hk", "japan", "australia", "canada", "europe",
  "metals", "energies", "indices",
];
const subLabel = (t, slug) =>
  SUB_KEYS.includes(slug)
    ? t(`homepage.spreads.subLabels.${slug}`, slug)
    : slug;

function fmt(n, sym) {
  if (!Number.isFinite(n)) return "·";
  if (/JPY$/.test(sym)) return n.toFixed(3);
  if (sym === "BTCUSD" || sym === "ETHUSD" || /^(US30|NAS100|SPX500|GER40|UK100|JPN225|HK50|DAX|FRA40|AUS200|IND50|RUS2K|EU50|SWI20|CHINA50|ESP35|ITA40|NETH25|SA40)/.test(sym))
    return Math.round(n).toLocaleString();
  if (sym === "USOIL" || sym === "UKOIL") return n.toFixed(2);
  if (sym === "NGAS") return n.toFixed(3);
  if (sym.length === 6) return n.toFixed(5);
  return n.toFixed(2);
}
// Index symbols whose price is quoted in whole points (not pips) ·
// these would otherwise fall into the `length === 6` FX branch and
// blow up the spread value by 10000× (e.g. NAS100 showing 13,300.0).
const INDEX_PATTERN = /^(WS30|SP500|NAS100|GER40|DAX40|UK100|FTSE100|JPN225|NIKKEI|HSI50|HK50|FRA40|CAC40|AUS200|ASX200|AEX25|SWI20|SMI20|ESP35|IBEX35|ITA40|NETH25|EU50|ESX50|SA40|RUS2K|CHINA50|IND50|SPX500)/;
// Metals (XAUUSD, XAGUSD, XPTUSD, XPDUSD) are 6-char tickers but their
// pip math is different — 1 pip = $0.10 for Gold, $0.001 for Silver.
// Without this guard they fall into the FX branch and the spread reads
// as "2300 pips" instead of "23 pips".
const METAL_PATTERN = /^(XAU|XAG|XPT|XPD)/;
function spread(bid, ask, sym) {
  if (!Number.isFinite(bid) || !Number.isFinite(ask)) return "·";
  const diff = ask - bid;
  if (/JPY$/.test(sym)) return (diff * 100).toFixed(1);
  if (INDEX_PATTERN.test(sym)) return diff.toFixed(2);
  if (METAL_PATTERN.test(sym)) {
    // Gold pip = $0.10 → multiplier 10
    // Silver pip = $0.001 → multiplier 1000 (same magnitude as FX-ish)
    if (/^XAG/.test(sym)) return (diff * 1000).toFixed(1);
    return (diff * 10).toFixed(1);
  }
  if (sym.length === 6) return (diff * 10000).toFixed(1);
  if (sym === "BTCUSD" || sym === "ETHUSD") return diff.toFixed(0);
  return diff.toFixed(2);
}
// Tick flash · animates the TEXT COLOUR (not a background box) so a
// rapid tick doesn't leave behind a persistent red/green rounded
// rectangle around the price. Green for up-ticks, red for down-ticks,
// fading back to the default text colour in 500 ms.
function FlashCell({ value, format, dir }) {
  return (
    <motion.span
      key={value}
      initial={{ color: dir === "up" ? "#54d97c" : dir === "down" ? "#f06a6a" : "var(--text-1)" }}
      animate={{ color: "var(--text-1)" }}
      transition={{ duration: 0.5 }}
      className="font-mono tabular-nums tabular-nums"
    >
      {format ? format(value) : value}
    </motion.span>
  );
}

const PAGE_SIZE = 10;  // User-requested · load 10 instruments at a time
                        // (Prev/Next pagination) so a category like
                        // Shares (859 rows) never takes over the screen.

export default function LiveSpreadsPanel() {
  const { t } = useTranslation();
  const { prices, status } = useLivePrices();
  const [cat, setCat] = useState("All");
  const [sub, setSub] = useState("all");
  const [query, setQuery] = useState("");
  const [tree, setTree] = useState([]);
  const [page, setPage] = useState(1);

  // Pull the live category taxonomy once · powers the subcategory chip row.
  useEffect(() => {
    api.get("/market/categories").then(({ data }) => setTree(data?.categories || [])).catch(() => {});
  }, []);

  const merged = useMemo(() => Object.values(prices), [prices]);

  const filtered = useMemo(() => {
    const q = query.trim().toUpperCase();
    const CAT_ORDER = { forex: 0, metals: 1, indices: 2, crypto: 3, energies: 4, commodities: 5, shares: 6, bonds: 7 };
    // Most-watched instruments per category bubble to the top of their bucket.
    const PRIORITY = {
      EURUSD: 1, GBPUSD: 2, USDJPY: 3, USDCHF: 4, AUDUSD: 5, USDCAD: 6, NZDUSD: 7,
      XAUUSD: 1, XAGUSD: 2, XPTUSD: 3, XPDUSD: 4,
      WS30: 1, SP500: 2, NAS100: 3, DAX40: 4, UK100: 5, JPN225: 6, HSI50: 7,
      BTCUSD: 1, ETHUSD: 2, SOLUSD: 3, XRPUSD: 4, BNBUSD: 5, ADAUSD: 6, DOGEUSD: 7,
      USOIL: 1, UKOIL: 2,
      AAPLUS: 1, MSFTUS: 2, NVDAUS: 3, TSLAUS: 4, AMZNUS: 5, GOOGLUS: 6, METAUS: 7,
    };
    return merged
      .filter((p) => {
        if (cat !== "All" && (p.category || "").toLowerCase() !== cat.toLowerCase()) return false;
        if (sub !== "all" && (p.subcategory || "").toLowerCase() !== sub.toLowerCase()) return false;
        if (q && !((p.symbol || "").toUpperCase().includes(q) || (p.name || "").toUpperCase().includes(q))) return false;
        return true;
      })
      .sort((a, b) => {
        // 1) Category order · 2) priority bump · 3) alphabetical
        const ca = CAT_ORDER[(a.category || "").toLowerCase()] ?? 9;
        const cb = CAT_ORDER[(b.category || "").toLowerCase()] ?? 9;
        if (ca !== cb) return ca - cb;
        const pa = PRIORITY[a.symbol] ?? 99;
        const pb = PRIORITY[b.symbol] ?? 99;
        if (pa !== pb) return pa - pb;
        return a.symbol.localeCompare(b.symbol);
      });
  }, [merged, cat, sub, query]);

  const counts = useMemo(() => {
    const c = { All: merged.length };
    CATEGORIES.slice(1).forEach((k) => { c[k] = merged.filter((m) => (m.category || "").toLowerCase() === k.toLowerCase()).length; });
    return c;
  }, [merged]);

  // Subcategory chips for the currently-active top category.
  const subList = useMemo(() => {
    if (cat === "All") return [];
    const node = tree.find((t) => t.name.toLowerCase() === cat.toLowerCase());
    if (!node || node.subcategories.length <= 1) return [];
    return node.subcategories;
  }, [cat, tree]);

  // Reset to page 1 when filter changes
  useEffect(() => { setPage(1); }, [cat, sub, query]);

  // Reset subcategory when top category changes.
  useEffect(() => { setSub("all"); }, [cat]);

  const isSearching = query.trim().length > 0;
  // 10-per-page pagination · so any category (even Shares 859) renders
  // a constant 10-row footprint and the page can't be hijacked by an
  // accidental tap on "Show more". Page state auto-resets when the
  // filter changes (see effect just above).
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <section id="live-spreads" data-testid="live-spreads-panel" className="relative py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-10">
          <div className="pill-mono inline-flex mb-5">
            <span className="relative flex h-1.5 w-1.5 mr-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: "#54d97c" }} />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: "#54d97c" }} />
            </span>
            {t("homepage.spreads.section.pill", "Live spreads")}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.025em] leading-[1.05] text-text-1">
            {t("homepage.spreads.section.head1", "Real-time spreads across")} <span className="text-gradient-brand font-semibold">{t("homepage.spreads.section.head2", "every asset class")}</span>
          </h2>
          <p className="text-text-3 mt-5 max-w-2xl mx-auto text-[15px] leading-relaxed">
            {t("homepage.spreads.section.sub", "Over {{count}} instruments updated in real time. Filter by class, then look at majors, minors, crosses or by exchange.", { count: (merged.length || 1200).toLocaleString() })}
          </p>
          {/* Feed source disclosure · the live stream is the Elite raw-spread
              feed (the deepest book we offer). Classic / Premier accounts
              add their own mark-up on top — see /account-comparison. */}
          <div
            className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[12px] font-mono uppercase tracking-[0.14em]"
            style={{
              borderColor: "color-mix(in srgb, var(--brand) 30%, transparent)",
              background: "color-mix(in srgb, var(--brand) 7%, transparent)",
              color: "var(--brand-deep)",
            }}
            data-testid="livespreads-feed-source"
          >
            <span className="font-semibold tracking-[0.18em]">{t("homepage.spreads.feedSource", "Elite · Raw Spread Feed")}</span>
            <span className="opacity-50">·</span>
            <span className="opacity-80">{t("homepage.spreads.feedNote", "Classic & Premier add mark-up")}</span>
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 mb-5">
          <ConnectionPill status={status} t={t} />

          <div className="relative w-full max-w-md mx-auto">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-3" strokeWidth={1.8} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("homepage.spreads.search", "Search symbol or name · EURUSD · Tesla · Gold")}
              className="w-full h-11 pl-10 pr-4 rounded-full border bg-[color:var(--surface-2)] text-[13px] text-text-1 placeholder:text-text-3 focus:outline-none focus:border-[color:var(--brand)] transition-colors text-center"
              style={{ borderColor: "var(--line-strong)" }}
              data-testid="livespreads-search"
            />
          </div>

          {/* Top-level category chips · explicit col-span grid so the
              7 categories lay out cleanly as 4+3 on mobile (no awkward
              wrap to 4-2-1) and 7×1 on desktop. Each chip fills its
              cell with comfortable internal padding. */}
          <div className="w-full" data-testid="livespreads-tabs">
            <div
              className="grid grid-cols-12 sm:grid-cols-7 gap-2.5 p-3 rounded-2xl border"
              style={{ borderColor: "var(--line-strong)", background: "var(--surface-2)" }}
            >
              {CATEGORIES.map((k, i) => {
                const active = cat === k;
                // First 4 chips → col-span-3 (so 4 fit in a 12-col row)
                // Last 3 chips → col-span-4 (so 3 fit in the 12-col row below)
                // Desktop · col-span-1 of 7 cols always
                const mobileSpan = i < 4 ? "col-span-3" : "col-span-4";
                return (
                  <button
                    key={k}
                    onClick={() => setCat(k)}
                    data-testid={`livespreads-cat-${k.toLowerCase()}`}
                    className={`${mobileSpan} sm:col-span-1 relative inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl text-[13px] font-medium whitespace-nowrap transition-colors`}
                    style={{
                      background: "var(--surface)",
                      color: active ? "var(--brand)" : "var(--text-2)",
                    }}
                  >
                    {t(`homepage.spreads.categories.${CATEGORY_I18N_KEY[k]}`, k)}
                    {/* Count badge · hidden on mobile so the chip label
                        has comfortable breathing room. */}
                    <span
                      className="hidden sm:inline-flex text-[12px] font-mono px-1.5 py-0.5 rounded-full tabular-nums"
                      style={{
                        background: "color-mix(in srgb, var(--surface-3) 80%, transparent)",
                        color: active ? "var(--brand)" : "var(--text-3)",
                      }}
                    >
                      {counts[k] ?? 0}
                    </span>
                    {/* Active underline · sits inside the chip's
                        bottom edge, signalling selection without
                        flooding the whole pill with brand fill. */}
                    {active && (
                      <motion.span
                        layoutId="livespreads-tab-indicator"
                        className="absolute left-3 right-3 bottom-1 h-[2px] rounded-full"
                        style={{ background: "var(--brand)" }}
                        transition={{ type: "spring", stiffness: 380, damping: 32 }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Subcategory chips · second-level filter · only shown when meaningful */}
          <AnimatePresence>
            {subList.length > 0 && (
              <motion.div
                key={`sub-${cat}`}
                initial={{ opacity: 0, y: -6, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: -6, height: 0 }}
                transition={{ duration: 0.25 }}
                className="w-full flex flex-wrap items-center justify-center gap-1.5 pt-1"
                data-testid="livespreads-subtabs"
              >
                <button
                  type="button"
                  onClick={() => setSub("all")}
                  data-testid="livespreads-sub-all"
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12px] font-medium transition-colors"
                  style={{
                    background: "var(--surface-2)",
                    color: sub === "all" ? "var(--brand)" : "var(--text-2)",
                    boxShadow: sub === "all" ? "inset 0 0 0 1px var(--brand)" : "inset 0 0 0 1px var(--line-strong)",
                  }}
                >
                  {t("homepage.spreads.subAll", "All {{label}}", { label: subLabel(t, cat.toLowerCase()) || cat })}
                </button>
                {subList.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => setSub(s.name)}
                    data-testid={`livespreads-sub-${s.name}`}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12px] font-medium transition-colors"
                    style={{
                      background: "var(--surface-2)",
                      color: sub === s.name ? "var(--brand)" : "var(--text-2)",
                      boxShadow: sub === s.name ? "inset 0 0 0 1px var(--brand)" : "inset 0 0 0 1px var(--line-strong)",
                    }}
                  >
                    {subLabel(t, s.name)}
                    <span
                      className="text-[12px] font-mono px-1.5 py-0 rounded-full tabular-nums"
                      style={{
                        background: "color-mix(in srgb, var(--surface-3) 80%, transparent)",
                        color: sub === s.name ? "var(--brand)" : "var(--text-3)",
                      }}
                    >
                      {s.count}
                    </span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Table · desktop has the full 7-column reference layout
            (Asset / Bid / Ask / Spread / Daily Change / Leverage /
            Platform). Mobile keeps the compact 4-column view
            (Asset / Bid / Ask / Δ%) so prices never overlap.
            The desktop grid uses an explicit fr-based template so
            column widths are RESPECTED regardless of the longest
            value (e.g. "up to 1:1000" can't shift the rest). */}
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}>
          {(() => {
            // Defined once and reused on header + each row · the
            // ratios mirror the user's reference table.
            return null;
          })()}
          {/* DESKTOP header */}
          <div
            className="hidden sm:grid gap-3 px-6 py-3 font-mono text-[12px] uppercase tracking-[0.18em] text-text-3 border-b items-center"
            style={{
              borderColor: "var(--line)",
              gridTemplateColumns: "minmax(150px, 1.6fr) 1fr 1fr 0.7fr 1fr 1.2fr 0.9fr",
            }}
          >
            <div>{t("homepage.spreads.table.asset", "Asset")}</div>
            <div className="text-right">{t("homepage.spreads.table.bid", "Bid")}</div>
            <div className="text-right">{t("homepage.spreads.table.ask", "Ask")}</div>
            <div className="text-right">{t("homepage.spreads.table.spread", "Spread")}</div>
            <div className="text-right">{t("homepage.spreads.table.dailyChange", "Daily Change")}</div>
            <div className="text-right">{t("homepage.spreads.table.leverage", "Leverage")}</div>
            <div className="text-right">{t("homepage.spreads.table.platform", "Platform")}</div>
          </div>
          {/* MOBILE header · Bid + Ask as separate columns */}
          <div className="grid sm:hidden grid-cols-12 gap-2 px-5 py-3 font-mono text-[9.5px] uppercase tracking-[0.16em] text-text-3 border-b" style={{ borderColor: "var(--line)" }}>
            <div className="col-span-4">{t("homepage.spreads.table.asset", "Asset")}</div>
            <div className="col-span-3 text-right">{t("homepage.spreads.table.bid", "Bid")}</div>
            <div className="col-span-3 text-right">{t("homepage.spreads.table.ask", "Ask")}</div>
            <div className="col-span-2 text-right">{t("homepage.spreads.table.changeShort", "Δ%")}</div>
          </div>

          {filtered.length === 0 && (
            <div className="px-4 py-12 text-center text-text-3 text-sm" data-testid="livespreads-empty">
              {status === "connecting" ? (
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand animate-pulse" />
                  {t("homepage.spreads.connecting", "Connecting to the live feed…")}
                </span>
              ) : (
                t("homepage.spreads.empty", "No symbols match. Try a different search or category.")
              )}
            </div>
          )}

          <ul>
            <AnimatePresence initial={false}>
              {visible.map((p) => {
                const up = (p.change ?? 0) >= 0;
                const IconCmp = SYMBOL_ICON[p.symbol] || CAT_ICON[p.category && p.category[0].toUpperCase() + p.category.slice(1)] || CircleDollarSign;
                const lev = leverageFor(p.symbol, p.category);
                return (
                  <motion.li
                    key={p.symbol}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="border-b last:border-b-0 hover:bg-[color:var(--surface-2)] transition-colors"
                    style={{ borderColor: "var(--line)" }}
                    data-testid={`livespreads-row-${p.symbol}`}
                  >
                    {/* DESKTOP row · 7 columns matching the header
                        template. Widths are fr-based so they NEVER
                        shift based on the longest value. */}
                    <div
                      className="hidden sm:grid gap-3 px-6 py-3 items-center"
                      style={{ gridTemplateColumns: "minmax(150px, 1.6fr) 1fr 1fr 0.7fr 1fr 1.2fr 0.9fr" }}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <SymbolIcon symbol={p.symbol} category={p.category} Fallback={IconCmp} size={28} />
                        <span className="text-text-1 text-[13.5px] font-medium truncate">{p.symbol}</span>
                      </div>
                      <div className="text-right text-text-1 text-[13px] font-medium tabular-nums">
                        <FlashCell value={p.bid} format={(v) => fmt(v, p.symbol)} dir={p.dir} />
                      </div>
                      <div className="text-right text-text-1 text-[13px] tabular-nums">
                        <FlashCell value={p.ask} format={(v) => fmt(v, p.symbol)} dir={p.dir} />
                      </div>
                      <div className="text-right text-text-2 font-mono text-[12px] tabular-nums">
                        {spread(p.bid, p.ask, p.symbol)}
                      </div>
                      <div className="text-right">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[12px] font-semibold tabular-nums whitespace-nowrap"
                          style={{
                            background: up ? "rgba(80,220,130,0.12)" : "rgba(240,106,106,0.12)",
                            border: `1px solid ${up ? "rgba(80,220,130,0.32)" : "rgba(240,106,106,0.32)"}`,
                            color: up ? "#54d97c" : "#f06a6a",
                          }}
                        >
                          {up ? <TrendingUp size={9} strokeWidth={2.6} /> : <TrendingDown size={9} strokeWidth={2.6} />}
                          {up ? "+" : ""}{(p.change ?? 0).toFixed(2)}%
                        </span>
                      </div>
                      <div className="text-right text-text-2 text-[12px] font-mono tabular-nums whitespace-nowrap">
                        {lev.upTo ? t("homepage.spreads.upTo", "up to {{ratio}}", { ratio: lev.ratio }) : lev.ratio}
                      </div>
                      <div className="text-right">
                        <span
                          className="inline-flex items-center justify-center font-mono text-[12px] uppercase tracking-[0.15em] px-2 py-1 rounded-md"
                          style={{
                            background: "color-mix(in srgb, var(--brand) 10%, transparent)",
                            color: "var(--brand)",
                            border: "1px solid color-mix(in srgb, var(--brand) 28%, transparent)",
                          }}
                        >
                          MT5
                        </span>
                      </div>
                    </div>

                    {/* MOBILE row · compact 4-column layout. Spread /
                        Leverage / Platform are intentionally OMITTED on
                        mobile to keep the row tap-friendly. Users can
                        tap through to /symbols for the full detail. */}
                    <div className="grid sm:hidden grid-cols-12 gap-2 px-5 py-3.5 items-center">
                      <div className="col-span-4 flex items-center gap-2 min-w-0">
                        <SymbolIcon symbol={p.symbol} category={p.category} Fallback={IconCmp} size={26} />
                        <span className="text-text-1 text-[13px] font-medium truncate">{p.symbol}</span>
                      </div>
                      <div className="col-span-3 text-right text-text-1 text-[12.5px] font-medium tabular-nums">
                        <FlashCell value={p.bid} format={(v) => fmt(v, p.symbol)} dir={p.dir} />
                      </div>
                      <div className="col-span-3 text-right text-text-2 text-[12.5px] tabular-nums">
                        <FlashCell value={p.ask} format={(v) => fmt(v, p.symbol)} dir={p.dir} />
                      </div>
                      <div className="col-span-2 text-right">
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[12px] font-semibold tabular-nums whitespace-nowrap"
                          style={{
                            background: up ? "rgba(80,220,130,0.12)" : "rgba(240,106,106,0.12)",
                            color: up ? "#54d97c" : "#f06a6a",
                          }}
                        >
                          {up ? "+" : ""}{(p.change ?? 0).toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </ul>

          {/* Footer · 10-per-page Prev/Next pagination + deep link to
              the full Symbol Explorer for power browsing. We never dump
              the long tail inline so the page can't be hijacked by an
              accidental Show More tap on Shares (859 rows). */}
          {filtered.length > 0 && (
            <div className="grid grid-cols-3 items-center border-t px-3 py-2.5" style={{ borderColor: "var(--line)" }}>
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="justify-self-start inline-flex items-center gap-1 h-9 px-3.5 rounded-full text-[12px] font-medium border disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[color:var(--surface-2)] transition-colors"
                style={{ borderColor: "var(--line-strong)", color: "var(--text-2)" }}
                data-testid="livespreads-prev"
              >
                <ChevronLeft size={14} /> {t("homepage.spreads.prev", "Prev")}
              </button>
              <div className="text-center text-[12px] font-mono tabular-nums text-text-3">
                {t("homepage.spreads.pageOf", "Page {{page}} of {{total}}", { page: safePage, total: totalPages })}
              </div>
              <button
                type="button"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="justify-self-end inline-flex items-center gap-1 h-9 px-3.5 rounded-full text-[12px] font-medium border disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[color:var(--surface-2)] transition-colors"
                style={{ borderColor: "var(--line-strong)", color: "var(--text-2)" }}
                data-testid="livespreads-next"
              >
                {t("homepage.spreads.next", "Next")} <ChevronRight size={14} />
              </button>
            </div>
          )}

          {!isSearching && filtered.length > PAGE_SIZE && (
            <a
              href={`/symbols?cat=${cat.toLowerCase()}${sub !== "all" ? `&sub=${sub}` : ""}`}
              data-testid="livespreads-open-explorer"
              className="block w-full py-2.5 border-t text-center text-[12px] font-medium hover:bg-[color:var(--surface-2)] transition-colors"
              style={{ borderColor: "var(--line)", color: "var(--brand)" }}
            >
              {t("homepage.spreads.openExplorer", "Open the full {{prefix}}explorer · {{count}} instruments →", {
                prefix: cat !== "All" ? `${t(`homepage.spreads.categories.${CATEGORY_I18N_KEY[cat]}`, cat).toLowerCase()} ` : "",
                count: filtered.length.toLocaleString(),
              })}
            </a>
          )}
        </div>

        <p className="text-text-3 text-[12px] text-center mt-4">
          {t("homepage.spreads.showing", "Showing {{visible}} of {{total}} instruments", { visible: visible.length, total: filtered.length })}
        </p>
      </div>
    </section>
  );
}

function ConnectionPill({ status, t }) {
  const isLive = status === "connected";
  const isFallback = status === "fallback";
  const label = isLive || isFallback ? t("homepage.spreads.statusLive", "Live") : status;
  const color = isLive || isFallback ? "#54d97c" : "var(--text-3)";
  return (
    <div
      className="inline-flex items-center gap-2 h-6 px-3 rounded-full text-[12px] font-mono uppercase tracking-wider border"
      style={{
        color,
        borderColor: isLive || isFallback ? "color-mix(in srgb, #54d97c 35%, transparent)" : "var(--line-strong)",
        background: isLive || isFallback ? "color-mix(in srgb, #54d97c 10%, transparent)" : "var(--surface-2)",
      }}
      data-testid="livespreads-status"
    >
      {isLive || isFallback ? <Wifi size={10} /> : <WifiOff size={10} />}
      {label}
    </div>
  );
}
